import { Button, Card, css, Paragraph, Stack } from 'canoe';
import { compose } from 'hammock';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { ApplicationId } from '@candu-ai/content/types';
import { DocumentPreview } from '@candu-ai/document/preview';
import { DocumentsSdk } from '@candu-ai/document/types';
import { FirstMate, TemplateIdentifier, useFirstMate } from '@candu-ai/first-mate/api';
import { useNavigation } from 'commons/hooks';
import { useSessionStorage } from 'commons/hooks/useSessionStorage';
import { Senstitives, withSensitives } from 'commons/sensitives';

type Awaited<T> = T extends PromiseLike<infer U> ? U : T;
// @ts-ignore
type TemplateContent = Awaited<ReturnType<FirstMate['templateContent']>>;

const Flex = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const StyledPrompt = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colors.GlitterGrayL20};
    padding: ${theme.spaces.s};
    border-radius: ${theme.borderRadius.small};
    margin-bottom: ${theme.spaces.s};
  `}
`;

const Grid = styled.div`
  ${({ theme }) => css`
    width: 706px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: ${theme.spaces.s};
    grid-row-gap: ${theme.spaces.s};
  `}
`;

const Transform = styled.div`
  height: 366px;
  left: 0;
  position: absolute;
  width: 683px;
  top: 0;
  transform: scale(0.5) translate(-50%, -50%);
`;

const LoadingCard = styled(Card)`
  ${({ theme }) => css`
    width: 344px;
    min-height: 185px;
    background-color: ${theme.colors.GlitterGrayL30};
    cursor: not-allowed;
  `}
`;

const StyledCard = styled(Card)`
  ${({ theme, active }) => css`
    width: 344px;
    min-height: 185px;
    border: 1px solid ${theme.colors.GlitterGrayL10};
    position: relative;
    z-index: 1;
    overflow: hidden;
    cursor: pointer;

    ${active &&
    css`
      border-color: ${theme.colors.AquaTeal};
    `};

    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 2;
    }
  `}
`;

const replaceInContent = (content: DocumentsSdk, key: string, value: string) => {
  // @ts-ignore
  Object.values(content.document.nodes).forEach((node: any) => {
    // todo: SRC in links and images
    if (typeof node?.props?.title === 'string') {
      // eslint-disable-next-line no-param-reassign
      node.props.title = node.props.title.replaceAll(`{{${key}}}`, value);
    }
    if (typeof node?.props?.children === 'string') {
      // eslint-disable-next-line no-param-reassign
      node.props.children = node.props.children.replaceAll(`{{${key}}}`, value);
    }
  });
};

/**
 * Mutates provided template
 * @param templateContent
 */
export const replacePlaceholders = (templateContent: TemplateContent, template: any) => {
  if (!templateContent) {
    return undefined;
  }

  Object.entries(templateContent.data).forEach(([key, value]) => {
    if (key !== 'content_name') {
      // @ts-ignore
      replaceInContent(template, key, value);
    }
  });

  return template;
};

interface TemplateAiPreviewProps {
  applicationId: ApplicationId;
  template: TemplateIdentifier;
  prompt: string;
  onClick: (templateContent: TemplateContent) => void;
  selectedTemplate?: number;
}

const TemplateAiPreview = ({
  template,
  prompt,
  applicationId,
  selectedTemplate,
  onClick,
}: TemplateAiPreviewProps) => {
  const firstMate = useFirstMate();
  const [templateToRender, setTemplateToRender] = useState<TemplateContent | undefined>();

  useEffect(() => {
    (async () => {
      if (!firstMate) {
        return;
      }
      const data = await firstMate.templateContent({ template, prompt });

      if (!data) {
        console.error('An error occurred');
        return;
      }
      setTemplateToRender(data);
    })();
  }, [template, prompt]);

  if (!templateToRender) {
    return <LoadingCard />;
  }

  return (
    <StyledCard
      active={selectedTemplate === templateToRender.templateId}
      padding="none"
      onClick={() => onClick(templateToRender)}
    >
      <Transform>
        <DocumentPreview
          applicationId={applicationId}
          documentId={templateToRender.templateId}
          isTemplate
          onGetDocument={(document: any) => replacePlaceholders(templateToRender, document)}
          checkeredBackdrop={false}
        />
      </Transform>
    </StyledCard>
  );
};

interface GeneratedTemplatesProps {
  applicationId: ApplicationId;
  sensitives: Senstitives;
  templates: TemplateIdentifier[];
  prompt: string;
}

export const GeneratedTemplatesImpl = ({
  templates,
  prompt,
  sensitives,
}: GeneratedTemplatesProps) => {
  const navigate = useNavigation();
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateContent | undefined>();
  const [, setTemplateContent] = useSessionStorage(
    `candu.firstMate.templateContent-${selectedTemplate?.templateId}`,
    undefined,
  );

  return (
    <>
      <Flex>
        <StyledPrompt>
          <Paragraph size="short" noMargin maxWidth="none">
            {prompt}
          </Paragraph>
        </StyledPrompt>
      </Flex>
      <Stack justify="center">
        <Grid>
          {templates.map((template) => (
            <TemplateAiPreview
              key={template}
              applicationId={sensitives[0].applicationId.toString()}
              template={template}
              prompt={prompt}
              onClick={(templateContent: TemplateContent) => setSelectedTemplate(templateContent)}
              selectedTemplate={selectedTemplate?.templateId}
            />
          ))}
        </Grid>
        <Button
          variant="secondary"
          disabled={!selectedTemplate}
          onClick={() => {
            setTemplateContent(JSON.stringify(selectedTemplate || {}));
            navigate(`/content/new?templateId=${selectedTemplate?.templateId}`);
          }}
        >
          Choose Design
        </Button>
      </Stack>
    </>
  );
};

export const GeneratedTemplates = compose(withSensitives)(GeneratedTemplatesImpl);
